import _ from "lodash";
import { api } from "careand-redux/api/ApiThunks";
import {
  ISSUE_TYPES,
  MEDICATION_STATUS,
} from "components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants";
import CPPUtils from "services/CPPUtils";
import moment from "moment-timezone";
import Bugsnag from "@bugsnag/js";
import MiscUtils from "services/MiscUtils";
import AdminUtils from "services/AdminUtils";
import PhysicianUtils from "services/PhysicianUtils";
import CalendarUtils from "services/CalendarUtils";

class AIUtils {
  getAssistantName() {
    return "Carey (AI Assistant)";
  }

  cleanBlock(text, blockType = "json") {
    const regex = new RegExp(`\`\`\`(?:${blockType})?(.*?)\`\`\``, "gs");
    const match = regex.exec(text);
    return match ? match[1].trim() : text.trim();
  }

  parseImmunizationsExtraction(extraction) {
    const DATE_FORMAT = "YYYY/MM/DD";
    return JSON.parse(extraction).map((val) => ({
      ...val,
      given_date: val.given_date
        ? moment(val.given_date, DATE_FORMAT).add(12, "hours").unix()
        : undefined,
      expiry_date: val.expiry_date
        ? moment(val.expiry_date, DATE_FORMAT).add(12, "hours").unix()
        : undefined,
    }));
  }

  async runNotePrompt(
    dispatch,
    patientId,
    name,
    note,
    existingItems,
    additionalFields = [],
  ) {
    let fields = [
      {
        name: "note",
        value: note,
      },
    ];

    if (existingItems) {
      fields.push({
        name: "existingItems",
        value: existingItems,
      });
    }
    try {
      let response = await dispatch(
        api.AI.runSavedPrompt({
          name,
          data: {
            patient_id: patientId,
            fields: [...fields, ...additionalFields],
          },
        }),
        {
          errorTitle: name,
          errorMessage: "Couldn't process your note. Please try again.",
        },
      );
      let result = response?.value || "[]";
      let json = JSON.parse(result);
      return json.result || json;
    } catch (err) {
      Bugsnag.notify(err);
      return [];
    }
  }

  async getAutofilledMetrics(dispatch, patientId, note) {
    const json = await this.runNotePrompt(
      dispatch,
      patientId,
      "Note Extraction - Metrics",
      note,
      undefined,
      undefined,
    );
    return json.filter(({ value }) => value);
  }

  async getPrefilledPreventativeScreenings(dispatch, patientId, note) {
    const json = await this.runNotePrompt(
      dispatch,
      patientId,
      "Note Extraction - Preventive Screenings",
      note,
    );
    return json.map((value) => {
      return { value };
    });
  }

  async getAutofilledAllergies(dispatch, patientId, note, records) {
    const allergies = CPPUtils.getActiveHealthRecords(
      records?.healthRecords,
      "allergies",
    );
    const existingItems = `${allergies.map((i) => i).join(", ")}` || "N/A";
    const json = await this.runNotePrompt(
      dispatch,
      patientId,
      "Note Extraction - Allergies",
      note,
      existingItems,
    );
    return json.map((value) => {
      return { value };
    });
  }

  async getAutofilledMedicalIssues(dispatch, patientId, note, records) {
    let values;
    try {
      const issues = (records?.healthRecords?.medical_issues || [])
        .filter((issue) => CPPUtils.isHealthRecordItemEnabled(issue))
        .map((issue) => issue.value.name);

      const existingItems = `${issues.map((i) => i).join(", ")}` || "N/A";

      let json = await this.runNotePrompt(
        dispatch,
        patientId,
        "Note Extraction - Issues",
        note,
        existingItems,
      );

      values = _.orderBy(json, ["issue_type", "name"], ["asc", "asc"]).map(
        (value) => {
          value.issue_type = ISSUE_TYPES[value.issue_type];
          return value;
        },
      );
    } catch (err) {
      values = [];
    }

    return values.map((value) => {
      return { value };
    });
  }

  async getAutofilledHistory(dispatch, patientId, note, records, historyType) {
    let values;
    try {
      const issues = (records?.healthRecords?.history || [])
        .filter(
          (issue) =>
            CPPUtils.isHealthRecordItemEnabled(issue) &&
            issue.value.history_type === historyType,
        )
        .map((issue) => issue.value.details);

      const existingItems = `${issues.map((i) => i).join(", ")}` || "N/A";

      const json = await this.runNotePrompt(
        dispatch,
        patientId,
        "Note Extraction - History",
        note,
        existingItems,
        [{ name: "historyType", value: historyType }],
      );

      values = _.orderBy(json, ["name"], ["asc"]).map((value) => {
        value.history_type = historyType;
        return value;
      });
    } catch (err) {
      values = [];
    }

    return values.map((value) => {
      return { value };
    });
  }

  async getAutofilledLifestyle(dispatch, patientId, note) {
    let values = await this.runNotePrompt(
      dispatch,
      patientId,
      "Note Extraction - Lifestyle",
      note,
    );
    return values.map((value) => {
      return { value };
    });
  }

  cleanDocuments(documents = []) {
    return documents.map((doc) => {
      return MiscUtils.cleanObject({
        ...doc,
        template_metadata: undefined,
      });
    });
  }

  async getAutofilledMedications(dispatch, patientId, note, records) {
    let values;
    try {
      const medications = (records?.healthRecords?.medications || [])
        .filter((medication) => CPPUtils.isHealthRecordItemEnabled(medication))
        .map(
          (medication) =>
            medication.value.prescription_medication?.name ||
            medication.value.name,
        );

      const existingItems = `${medications.map((i) => i).join(", ")}` || "N/A";

      const json = await this.runNotePrompt(
        dispatch,
        patientId,
        "Note Extraction - Medications",
        note,
        existingItems,
      );

      values = json.map((val) => {
        return {
          ...val,
          status: MEDICATION_STATUS.continue.name,
        };
      });
    } catch (e) {
      values = [];
    }
    return values.map((value) => {
      return { value };
    });
  }

  getStaffName(staffId, admins = [], physicians = []) {
    const admin = admins.find(({ id }) => id === staffId);
    const physician = physicians.find(({ id }) => id === staffId);
    let name = staffId;
    if (admin) name = AdminUtils.getName(admin);
    else if (physician) name = PhysicianUtils.getName(physician);
    return name;
  }

  getCommentsString(comments) {
    return comments.map(({ author_id, date, text }) => {
      return `${this.getStaffName(author_id)} (${CalendarUtils.fromNow(
        date,
      )}): ${text}`;
    });
  }

  convertDocument(doc, index = 0) {
    let str = `${index + 1}. ${doc.title} [${
      doc.document_type
    }], ${CalendarUtils.fromNow(doc.date)}`;

    if (doc.comments) str += `\n  - Patient-facing comments: ${doc.comments}`;

    let otherComments = [
      ...(doc.referral_comments || []),
      ...(doc.general_comments || []),
    ];

    if (otherComments.length > 0) {
      str += `\n  - Private comments:\n`;
      str += this.getCommentsString(otherComments)
        .map((text) => `    - ${text}`)
        .join("\n");
    }
    return str;
  }

  convertReminder(reminder) {
    return `Patient Reminder (${
      (CalendarUtils.format(reminder.date),
      CalendarUtils.fromNow(reminder.date))
    }) - ${reminder.message}`;
  }

  convertNote({ value, author_id }) {
    return `Appointment Note (${CalendarUtils.format(
      value.date,
    )}, ${CalendarUtils.fromNow(value.date)}), Author ID: ${author_id}\n\n${
      value.value
    }`;
  }

  convertReferralRequest({
    date,
    instructions,
    send_to = {},
    urgent = false,
    physician_id,
  }) {
    return `Referral Request (${CalendarUtils.format(
      date,
    )}, ${CalendarUtils.fromNow(
      date,
    )}), Author ID: ${physician_id}, Urgent: ${urgent}, Send to:${
      send_to.name
        ? `${send_to.name}, ${send_to.specialty}`
        : "Pending selection"
    }\nInstructions: ${instructions}`;
  }

  convertPrescription(prescription) {
    const formattedMeds = prescription.medications
      .map((med) => CPPUtils.getPrescriptionMedicationSummary(med))
      .join("\n");
    return `Prescription (${CalendarUtils.format(
      prescription.date,
    )}, ${CalendarUtils.fromNow(prescription.date)})\n${formattedMeds}`;
  }

  convertImmunization(immunization, type = "Immunization") {
    console.log(immunization);
    const description = CPPUtils.getImmunizationDescription(immunization);
    return `${type} (${CalendarUtils.format(
      immunization.given_date,
    )}, ${CalendarUtils.fromNow(immunization.given_date)})\n${description}`;
  }
}

export default new AIUtils();
